"use client";

import {
  AlertTriangle,
  ArrowDownCircle,
  Bell,
  CheckCircle,
  CircleDollarSign,
  Clock,
  CreditCard,
  FileText,
  Info,
  Mail,
  PlusCircle,
  RefreshCw,
  RotateCcw,
  Ruler,
  Send,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  UserCheck,
  Users,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import {
  TicketNotification,
  NotificationType,
} from "@/types/ticketNotifications";
import { useNotificationContext } from "@/context/NotificationContext";
import LoadingSpinner from "@/components/ui/loading-spinner";
import { Button } from "@/components/ui/button";
import { InlineErrorBoundary } from "@/utils/withErrorBoundary";

interface NotificationListProps {
  className?: string;
  maxHeight?: string;
  showHeader?: boolean;
  onNotificationClick?: (notification: TicketNotification) => void;
}

// Safe wrapper for useNotificationContext that returns defaults when provider is not available
function useSafeNotificationContext() {
  try {
    return useNotificationContext();
  } catch {
    // Return defaults when provider isn't mounted (user not authenticated)
    return {
      notifications: [],
      unreadCount: 0,
      loading: false,
      error: null,
      hasMore: false,
      loadingMore: false,
      markAsRead: async () => {},
      markAllAsRead: async () => {},
      createNotification: async () => {},
      refreshNotifications: async () => {},
      loadMoreNotifications: async () => {},
    };
  }
}

function NotificationListInner({
  className = "",
  maxHeight = "400px",
  showHeader = true,
  onNotificationClick,
}: NotificationListProps) {
  const {
    notifications,
    unreadCount,
    loading,
    error,
    hasMore,
    loadingMore,
    markAsRead,
    markAllAsRead,
    loadMoreNotifications,
  } = useSafeNotificationContext();

  if (loading) {
    return (
      <div className={`p-4 ${className}`}>
        <LoadingSpinner size="sm" />
        <span className="ml-2 text-sm text-muted-foreground">
          Loading notifications...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 text-error ${className}`}>
        <p className="text-sm">Error loading notifications: {error}</p>
      </div>
    );
  }

  const handleNotificationClick = async (notification: TicketNotification) => {
    // Mark only this specific notification as read when clicked
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Call optional click handler
    onNotificationClick?.(notification);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  return (
    <div
      className={`bg-card border border-border rounded-lg shadow-lg ${className}`}
    >
      {showHeader && (
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-foreground">
                Notifications
              </h3>
              {unreadCount > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none tabular-nums bg-destructive text-destructive-foreground rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <Button onClick={handleMarkAllAsRead} variant="outline" size="sm">
                Mark all read
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="overflow-y-auto" style={{ maxHeight }}>
        {notifications.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">
            <Bell
              className="w-12 h-12 mx-auto mb-4 text-muted-foreground"
              aria-hidden
            />
            <p className="text-sm">No notifications yet</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {/* All notifications */}
            {notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={handleNotificationClick}
              />
            ))}

            {/* Load More button */}
            {hasMore && (
              <div className="p-4 text-center border-t border-border">
                <Button
                  onClick={loadMoreNotifications}
                  variant="outline"
                  size="sm"
                  disabled={loadingMore}
                  className="w-full"
                >
                  {loadingMore ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Loading...</span>
                    </>
                  ) : (
                    "Load more"
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface NotificationItemProps {
  notification: TicketNotification;
  onClick: (notification: TicketNotification) => void;
}

function NotificationItem({ notification, onClick }: NotificationItemProps) {
  const isUnread = !notification.read;
  const createdAt = new Date(notification.created_at);
  const timeAgo = getTimeAgo(createdAt);

  const handleClick = () => {
    onClick(notification);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`
        block w-full p-4 text-left cursor-pointer transition-colors duration-150
        hover:bg-muted
        ${isUnread ? "bg-info-muted border-l-4 border-info" : ""}
      `}
    >
      <div className="flex items-start space-x-3">
        <NotificationIcon type={notification.type} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p
              className={`text-sm font-medium ${
                isUnread ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {getNotificationTitle(notification.type)}
            </p>
            <span className="text-xs text-muted-foreground ml-2 shrink-0">
              {timeAgo}
            </span>
          </div>

          <p
            className={`text-sm mt-1 ${
              isUnread ? "text-foreground" : "text-muted-foreground"
            }`}
          >
            {getNotificationMessage(notification)}
          </p>

          {notification.note && (
            <p className="text-xs text-muted-foreground mt-1 italic">
              &quot;{notification.note}&quot;
            </p>
          )}

          {isUnread && (
            <div className="mt-2 flex items-center">
              <div className="w-2 h-2 bg-info rounded-full"></div>
              <span className="ml-2 text-xs text-info font-medium">New</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

type NotificationIconConfig = {
  Icon: LucideIcon;
  className: string;
};

const notificationIconMap: Record<NotificationType, NotificationIconConfig> = {
  ticket_created: { Icon: PlusCircle, className: "text-info" },
  ticket_updated: { Icon: RefreshCw, className: "text-warning" },
  ticket_approved: { Icon: CheckCircle, className: "text-success" },
  ticket_rejected: { Icon: XCircle, className: "text-error" },
  ticket_completed: { Icon: CheckCircle, className: "text-info" },
  payment_required: { Icon: CreditCard, className: "text-warning" },
  manufacturer_matched: { Icon: Users, className: "text-info" },
  status_change: { Icon: ArrowDownCircle, className: "text-muted-foreground" },
  ticket_paid: { Icon: CreditCard, className: "text-success" },
  measurements_submitted: { Icon: Ruler, className: "text-success" },
  ticket_stale: { Icon: Clock, className: "text-muted-foreground" },
  credit_purchase: { Icon: CircleDollarSign, className: "text-success" },
  payout_transfer_started: { Icon: Send, className: "text-info" },
  payout_transfer_processing: { Icon: RefreshCw, className: "text-warning" },
  payout_transfer_completed: { Icon: CheckCircle, className: "text-success" },
  payout_transfer_failed: { Icon: XCircle, className: "text-error" },
  payout_transfer_refunded: { Icon: RotateCcw, className: "text-warning" },
  wise_transfer_update: {
    Icon: ArrowDownCircle,
    className: "text-muted-foreground",
  },
  wise_balance_credit: { Icon: PlusCircle, className: "text-success" },
  wise_balance_update: { Icon: RefreshCw, className: "text-info" },
  wise_account_state_change: { Icon: Info, className: "text-warning" },
  wise_verification_required: { Icon: ShieldAlert, className: "text-error" },
  wise_kyc_review_update: { Icon: ShieldCheck, className: "text-warning" },
  wise_dispute_update: { Icon: AlertTriangle, className: "text-error" },
  system_alert: { Icon: AlertTriangle, className: "text-error" },
  payment_received: { Icon: CreditCard, className: "text-success" },
  payment_confirmed: { Icon: CheckCircle, className: "text-success" },
  quote_received: { Icon: FileText, className: "text-success" },
  manufacturer_approved: { Icon: UserCheck, className: "text-success" },
  admin_message: { Icon: Mail, className: "text-info" },
  ticket_suggestion: { Icon: Sparkles, className: "text-info" },
  winback_offer: { Icon: CreditCard, className: "text-success" },
};

function NotificationIcon({ type }: { type: NotificationType }) {
  const { Icon, className } =
    notificationIconMap[type] ?? notificationIconMap.status_change;

  return <Icon className={`w-6 h-6 shrink-0 ${className}`} aria-hidden />;
}

function getNotificationTitle(type: NotificationType): string {
  const titleMap: Record<NotificationType, string> = {
    ticket_created: "Design created",
    ticket_updated: "Design updated",
    ticket_approved: "Design approved",
    ticket_rejected: "Design rejected",
    ticket_completed: "Dress completed",
    payment_required: "Payment required",
    manufacturer_matched: "Manufacturer matched",
    status_change: "Status update",
    ticket_paid: "Payment confirmed",
    measurements_submitted: "Measurements received",
    ticket_stale: "Inactive dress request",
    credit_purchase: "Credits purchased",
    payout_transfer_started: "Payout started",
    payout_transfer_processing: "Payout processing",
    payout_transfer_completed: "Payout completed",
    payout_transfer_failed: "Payout failed",
    payout_transfer_refunded: "Payout refunded",
    wise_transfer_update: "Transfer update",
    wise_balance_credit: "Credits added to balance",
    wise_balance_update: "Balance update",
    wise_account_state_change: "Account status changed",
    wise_verification_required: "Verification required",
    wise_kyc_review_update: "Identity verification update",
    wise_dispute_update: "Dispute update",
    system_alert: "System alert",
    payment_received: "Payment received",
    payment_confirmed: "Payment confirmed",
    quote_received: "Quote received",
    manufacturer_approved: "Manufacturer approved",
    admin_message: "Message from Build-a-Dress",
    ticket_suggestion: "Dress suggestion",
    winback_offer: "Discount offer",
  };

  return titleMap[type] || "Notification";
}

function getNotificationMessage(notification: TicketNotification): string {
  const messageMap: Record<NotificationType, string> = {
    ticket_created: "Your dress design request has been created",
    ticket_updated: "Your dress design has been updated",
    ticket_approved: "Your dress design has been approved!",
    ticket_rejected: "Your design needs revisions",
    ticket_completed: "Your custom dress is ready!",
    payment_required: "Complete payment to start production",
    manufacturer_matched: "A manufacturer will review your design",
    status_change: "Your design status has been updated",
    ticket_paid: "Payment received. We’ll start making your dress soon.",
    measurements_submitted: "Your measurements have been received",
    ticket_stale: "You haven’t updated this dress request in a while",
    credit_purchase: "Credits have been added to your account",
    payout_transfer_started: "Your payout has started",
    payout_transfer_processing: "Payout is being processed",
    payout_transfer_completed: "Your payout is complete",
    payout_transfer_failed: "Payout transfer has failed",
    payout_transfer_refunded: "Payout has been refunded",
    wise_transfer_update: "Wise transfer status has been updated",
    wise_balance_credit: "Wise account balance has been credited",
    wise_balance_update: "Wise account balance has been updated",
    wise_account_state_change: "Wise account state has changed",
    wise_verification_required: "Wise account verification is required",
    wise_kyc_review_update:
      "Your Wise identity verification status has changed",
    wise_dispute_update: "Wise transaction dispute has been updated",
    system_alert: "System alert notification",
    payment_received: "A customer has paid for their order",
    payment_confirmed: "Your payment has been confirmed",
    quote_received: "A manufacturer has submitted a quote for your design",
    manufacturer_approved: "Your manufacturer application has been approved",
    admin_message: "You have a new message from the Build-a-Dress team",
    ticket_suggestion: "We have a dress suggestion for you!",
    winback_offer: "A discount is available for your dress",
  };

  return messageMap[notification.type] || "You have a new notification";
}

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return "Just now";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks}w ago`;
  }

  // For older notifications, show the actual date
  return date.toLocaleDateString();
}

export default function NotificationList(props: NotificationListProps = {}) {
  return (
    <InlineErrorBoundary
      fallbackType="small"
      componentName="NotificationList"
      customFallback={({ resetError }) => (
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-card border border-border rounded-lg shadow-sm p-6">
            <div className="text-center">
              <div className="w-10 h-10 bg-info rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-info text-lg">🔔</span>
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                Notifications unavailable
              </h3>
              <p className="text-muted-foreground mb-4">
                There was an issue loading your notifications.
              </p>
              <Button
                onClick={resetError}
                className="px-4 py-2 rounded transition"
                variant="primary"
                size="md"
              >
                Reload notifications
              </Button>
            </div>
          </div>
        </div>
      )}
    >
      <NotificationListInner {...props} />
    </InlineErrorBoundary>
  );
}
